import React from "react";

import { LogOut, AlertTriangle, X } from "lucide-react";

import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";

interface SignOutConfirmationModalProps {
  isOpen: boolean;
  toggle: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

const SignOutConfirmationModal: React.FC<SignOutConfirmationModalProps> = ({
  isOpen,
  toggle,
  onConfirm,
  loading = false,
}) => {
  return (
    <CommonModal
      isOpen={isOpen}
      toggle={toggle}
      title=""
      size="md"
      showHeader={false}
      className="admin-signout-modal"
    >
      <div className="flex flex-col">
        {/* Custom Header */}
        <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shadow-sm">
              <LogOut size={20} className="text-red-600" strokeWidth={2.5} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 leading-tight">
                Sign Out
              </h2>
              <p className="text-xs text-gray-500">Confirm your action</p>
            </div>
          </div>
          <button
            onClick={toggle}
            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md p-1.5 transition-all duration-200"
            aria-label="Close"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {/* Content */}
        <div className="mb-5">
          <p className="text-sm text-gray-900 mb-4 font-medium">
            Are you sure you want to sign out of your account?
          </p>

          {/* Warning Box */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <AlertTriangle
                  className="w-5 h-5 text-orange-600"
                  strokeWidth={2}
                />
              </div>
              <div>
                <p className="text-sm text-orange-900 leading-relaxed">
                  You&apos;ll need to sign in again to access the admin
                  dashboard and manage organizations.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={toggle}
            disabled={loading}
            className="min-w-[100px]"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            loading={loading}
            icon={<LogOut size={16} className="text-white" strokeWidth={2.5} />}
            className="min-w-[120px] shadow-sm hover:shadow-md transition-shadow"
          >
            Sign Out
          </Button>
        </div>
      </div>
    </CommonModal>
  );
};

export default SignOutConfirmationModal;
